import React, { Component } from 'react'
import { toast } from 'sonner'
import { handleAndReportError } from '../../helpers/error_service'

class ErrorBoundary extends Component {
    constructor(props) {
        super(props)
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        }
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return {
            hasError: true,
            error: error
        }
    }

    componentDidCatch(error, errorInfo) {
        // Log error details
        console.error('[ERROR BOUNDARY] Application error caught:', error, errorInfo)

        // Update state with error info
        this.setState({
            error: error,
            errorInfo: errorInfo
        })

        // Show toast notification to user
        toast.error('An unexpected error occurred. Please reload the page.', {
            duration: Infinity
        })

        // Report error to API
        handleAndReportError(error, errorInfo)
    }

    handleReload = () => {
        // Reload the page to reset the app
        window.location.reload()
    }

    render() {
        if (this.state.hasError) {
            // Fallback UI
            return (
                <div style={styles.container}>
                    <div style={styles.content}>
                        <div style={styles.iconContainer}>
                            <svg
                                style={styles.icon}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                />
                            </svg>
                        </div>
                        <h1 style={styles.title}>Oops! Something went wrong</h1>
                        <p style={styles.message}>
                            We're sorry, but something unexpected happened. The error has been
                            reported to our team.
                        </p>
                        <button onClick={this.handleReload} style={styles.button}>
                            Reload Page
                        </button>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details style={styles.details}>
                                <summary style={styles.summary}>Error Details (Development Only)</summary>
                                <pre style={styles.errorText}>
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}

// Inline styles for the error boundary fallback UI
const styles = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f7fafc',
        padding: '1rem'
    },
    content: {
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    iconContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '1rem'
    },
    icon: {
        width: '64px',
        height: '64px',
        color: '#f56565'
    },
    title: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: '#2d3748',
        marginBottom: '0.5rem'
    },
    message: {
        fontSize: '1rem',
        color: '#4a5568',
        marginBottom: '1.5rem',
        lineHeight: '1.5'
    },
    button: {
        backgroundColor: '#556ee6',
        color: 'white',
        padding: '0.75rem 1.5rem',
        border: 'none',
        borderRadius: '4px',
        fontSize: '1rem',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        outline: 'none'
    },
    details: {
        marginTop: '1.5rem',
        textAlign: 'left',
        backgroundColor: '#fff5f5',
        padding: '1rem',
        borderRadius: '4px',
        border: '1px solid #feb2b2'
    },
    summary: {
        cursor: 'pointer',
        fontWeight: '600',
        color: '#c53030',
        marginBottom: '0.5rem'
    },
    errorText: {
        fontSize: '0.875rem',
        color: '#742a2a',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight: '300px',
        overflow: 'auto'
    }
}

export default ErrorBoundary
